
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient} from 'react-query';
import clsx from 'clsx';
import { Transforms, Editor } from 'slate'
import { useSlate, ReactEditor } from 'slate-react';
import { toast } from 'components/Shared/Toast';
import { getParent } from '@udecode/slate-plugins';
import styled from 'styled-components';
import { 
    Button, 
    ButtonGroup, 
    DropdownItem, 
    DropdownMenu, 
    DropdownToggle, 
    UncontrolledDropdown, 
    ModalFooter, 
    FormGroup, 
    Card,
    ModalBody, 
    ModalHeader, 
    Modal 
} from 'reactstrap';

import bootbox from 'components/Modals/Bootbox';
import { addSuggestion } from 'api/actions/suggestions'
import { fetcher } from 'api/client'
import { truncate, formatDistanceToNow } from 'utils'
import { isAlignBlock, getNodesById } from './editor-utils'
import { useProfileAbility } from 'context/profile';
import { useEditorActions, useEditorState, useEditorDispatch, SET_IS_FETCHING } from '../../editor-context';
import { compareElements } from 'api/actions/contracts'


const negotiableElements = ['clause', 'statement', 'text'];

const ElementMenuRoot = styled.div`
    position: absolute;
    top: .5rem;
    right: .5rem;
    user-select: none;
    display: flex;
`;

const ElementMenuIcon = styled.div`
    position: absolute;
    top: .5rem;
    right: .5rem;
    user-select: none;
    display: flex;
`;
const CompareContainer = styled.div`
    border: 1px solid #e4e4e4;
    background: #f7f7f7;
    padding: 10px;
    margin-bottom: 10px;
    border-radius: .25rem;
    padding-right: 10px;
`;

function ElementMenu({ element }) {
    const { t } = useTranslation()
    const editor = useSlate()
    const isOpenRef = useRef(false);
    const { document: contract, isEditable, mode: editorMode = 'contract' } = useEditorState();
    const dispatch = useEditorDispatch();
    const { goToTab, changeElementVersion } = useEditorActions();
    // const menuRef = useRef(false);
    const [isOpen, setIsOpen] = useState(isOpenRef.current);
    const profileAbility = useProfileAbility();
    // useOnClickOutside(menuRef, () => {
    //     setIsOpen(false)
    //     isOpenRef.current = false
    // })
    const { isVotable, voteStarted, locked: contentLocked, milestones = [] } = contract;
    const {
        type,
        isNonVotable,
        smartclause = {},
        versions = [],
        currentVersion,
        currentVersionId,
        currentLocalVersionId,
        reviewComments = [],
        signComments = [],
        milestoneId = '',
        isNegotiable,
    } = element;

    const hasComments = reviewComments.length || signComments.length;
    const isOwner = profileAbility.can('modify', contract);
    const canViewMilestones = profileAbility.can('read', contract, 'milestones'); // VIEW_MILESTONES
    const onError = (error, variables = {}) => {
        toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
    }
    const onMutate = (error) => {
        dispatch({ type: SET_IS_FETCHING, isFetching: true });
    }
    const onSettled = (error) => {
        dispatch({ type: SET_IS_FETCHING, isFetching: false });
    }

    const { mutate: mutateCompareElements } = useMutation((payload) => fetcher(compareElements(payload)), {
        onMutate,
        onError,
        onSettled,
    })

    useEffect(() => {
        setIsOpen(isOpenRef.current)
    }, [])

    const changeElementProperty = (propertyName, propertyValue) => {
        if (!isEditable) return;
        const path = ReactEditor.findPath(editor, element)
        // setCurrentElement({ element, location: { path, offset: 0 } });

        Transforms.setNodes(editor, { [propertyName]: propertyValue }, {
            at: path,
            match: (node) => node._id === element._id
        })
    }
    const isNew = useMemo(() => {
        if (!element || !element._id) return true;
        const path = ReactEditor.findPath(editor, element)
        const [parentNode] = getParent(editor, path) || [{}];

        if (isAlignBlock(parentNode.type)) {
            return parentNode.isNew || false
        }

        return element.isNew || false
    }, [element])

    const toggleBoolean = (propertyName, propertyValue) => {
        if (contentLocked) return;
        changeElementProperty(propertyName, !propertyValue)
    }

    const toggleVotable = () => toggleBoolean('isNonVotable', isNonVotable)
    const toggleNegotiable = () => { if (isOwner) toggleBoolean('isNegotiable', isNegotiable) }

    const handleChangeMilestone = (milestoneId) => () => {
        changeElementProperty('milestoneId', milestoneId)
    }
    

    const updateAllNodes = (editor, properties) => {
        const nodes = [...Editor.nodes(editor, {
            at: {
                anchor: { path: [0, 0], offset: 0 },
                focus: { path: [editor.children.length - 1, 0], offset: 0 }
            },
            match: (n) => negotiableElements.includes(n.type)
        })]

        nodes.map(([node, location]) => {
            Transforms.setNodes(editor, properties, {
                at: location
            })
        })

    }

    const changeNegotiable = (val) => async (e) => {
        if (contentLocked) return;
        switch (val) {
            case 'isNegotiable': changeElementProperty('isNegotiable', true); break;
            case 'notNegotiable': changeElementProperty('isNegotiable', false); break;
            case 'all':
                const res = await bootbox.confirm(t('editor.items.all_negotiable_confirm'));
                if (!res) return;
                updateAllNodes(editor, { isNegotiable: true })

                break;
            case 'none':
                const resnone = await bootbox.confirm(t('editor.items.none_negotiable_confirm'));
                if (!resnone) return;

                updateAllNodes(editor, { isNegotiable: false })
                break;
        }
    }

    const handleSelectVersion = (version) => () => {
        const [,path] = getNodesById(editor, element._id)[0];
        if (version._id !== element.currentVersionId) {
            mutateCompareElements({
                contractId: contract._id,
                version1: version._id, 
                elementId: element._id
            }, {
                onSuccess: (data) => {
                    // console.log({data, compare: element.currentLocalVersionId !== element.currentVersionId, currentVersionId: element.currentVersionId, currentLocalVersionId: element.currentLocalVersionId})
                    Transforms.setNodes(editor, {
                        currentLocalVersionId: version._id,
                        currentLocalVersion: version,
                        compareHTML: data.compare
                    }, { at: path })
                }
            })
        } else {
            Transforms.setNodes(editor, {
                currentLocalVersionId: version._id,
                currentLocalVersion: version,
            }, { at: path })
        }
        // changeElementVersion({
        //   version,
        //   elementPath: path,
        //   element
        // })
    }

    const handleGoToComments = () => {
        goToTab('comments')
    }

    const toggle = () => {
        setIsOpen(!isOpen)
        isOpenRef.current = !isOpen
    }


    return (
        <ElementMenuRoot contentEditable={false}>
            {isVotable && isNonVotable && voteStarted &&
                <span><b>RESERVADO</b></span>
            }
            {isVotable && isNonVotable && !voteStarted &&
                <span onClick={toggleVotable} ><i className="fas fa-thumbs-down text-muted align-middle ml-2" title="No Votable" contentEditable={false}></i></span>
            }
            {isVotable && !isNonVotable && !voteStarted &&
                <span onClick={toggleVotable} ><i className="fas fa-thumbs-up text-muted align-middle ml-2" title="Votable" contentEditable={false}></i></span>
            }
            {negotiableElements.includes(element.type) &&
                <>
                    {smartclause && smartclause.executionType &&
                       <span><i className="fas fa-hand-holding-usd align-middle ml-2 {{#if smartclause.amount}}text-primary{{else}}text-muted{{/if}}" data-action="open-smart-clause" contentEditable={false}></i></span>
                    }
                    {!!versions.length &&
                        <UncontrolledDropdown  contentEditable={false}>
                            <DropdownMenu  contentEditable={false}>
                                <DropdownItem className="font-weight-bold border-bottom p-2 text-dark" disabled contentEditable={false}>{t('editor.items.versions')}</DropdownItem>
                                {versions.map(version =>
                                    <DropdownItem key={version._id} className={clsx('p-2', version._id === (currentLocalVersionId || currentVersionId) && 'text-primary')} data-id={version._id} onClick={handleSelectVersion(version)}>
                                        {t('editor.items.version')}{' '}
                                        {version.number === 0 ? t('sign.contract.negotiation_original') : version.number}{' '} 
                                        {version._id === currentVersionId && `(${t('editor.items.actual')})`}
                                    </DropdownItem>
                                )}
                            </DropdownMenu>
                            <DropdownToggle tag="span"><i className="fas fa-file-alt align-middle ml-2 text-muted" title="Versiones" data-toggle="dropdown"></i></DropdownToggle>
                        </UncontrolledDropdown>
                    }
                    <span>
                        <i
                            className={clsx('align-middle ml-2', isNegotiable && 'fas fa-handshake text-primary', !isNegotiable && 'fas fa-handshake-slash text-muted')}
                            title={isNegotiable ? t('editor.items.negotiable') : t('editor.items.not_negotiable')}
                            onClick={toggleNegotiable}
                            contentEditable={false}
                            data-slate-element="element"
                            data-slate-void="true"
                            // {{#if isOwner}}data-action="toggleNegotiableBtn"{{/if}}
                        />
                    </span>
                </>
            }
                {editorMode === 'contract' && !isNew && type !== 'signature' && 
                    <span contentEditable={false} className="px-1">
                        
                        <i
                            className={clsx('fas fa-circle align-middle ml-2', !hasComments && 'text-muted', hasComments && 'text-primary')}
                            title={t('editor.comments.title')}
                            data-action={reviewComments.length ? 'comment' : 'signcomment'}
                            onClick={handleGoToComments}
                            contentEditable={false}
                        />
                        
                    </span>
                    
                }
                {editorMode === 'contract' &&
                    <UncontrolledDropdown contentEditable={false}>
                        <DropdownToggle contentEditable={false} tag="span">
                            <span size="sm" className="border-0" onClick={toggle}>
                                <small><i className="fas fa-ellipsis-v text-muted ml-2"></i></small>
                            </span>
                        </DropdownToggle>   
                        <DropdownMenu contentEditable={false}>
                            {editorMode === 'contract' && !isNew &&
                                <>
                                    <DropdownItem className="font-weight-bold py-3">{t('editor.comments.title')}</DropdownItem>
                                    <DropdownItem className="py-3" data-action="comment">{reviewComments.length} {t('editor.items.collaboration')}</DropdownItem>
                                    <DropdownItem className="py-3" data-action="signcomment">{signComments.length} {t('editor.items.participants')}</DropdownItem>
                                </>
                            }

                            {negotiableElements.includes(type) &&
                                <>
                                    <DropdownItem className="border-top font-weight-bold py-3">{t('sign.menu.negotiation_tab')}</DropdownItem>
                                    <DropdownItem className={clsx('py-3', contentLocked && 'negotiation-disabled')} id="all" onClick={changeNegotiable('all')}><i className="fas fa-circle text-muted mr-2"></i> {t('editor.items.all_negotiable')}</DropdownItem>
                                    <DropdownItem className={clsx('py-3', contentLocked && 'negotiation-disabled')} id="none" onClick={changeNegotiable('none')}><i className="fas fa-circle text-muted mr-2"></i> {t('editor.items.none_negotiable')}</DropdownItem>
                                    <DropdownItem className={clsx('py-3', contentLocked && 'negotiation-disabled')} id="isNegotiable" onClick={changeNegotiable('isNegotiable')}><i className={clsx('fas mr-2', isNegotiable ? 'fa-check-circle text-primary' : 'fa-circle text-muted')} /> {t('editor.items.negotiable')}</DropdownItem>
                                    <DropdownItem className={clsx('py-3', contentLocked && 'negotiation-disabled')} id="notNegotiable" onClick={changeNegotiable('notNegotiable')}><i className={clsx('fas mr-2', !isNegotiable ? 'fa-check-circle text-primary' : 'fa-circle text-muted')} /> {t('editor.items.not_negotiable')}</DropdownItem>
                                </>
                            }

                            {canViewMilestones && !!milestones.length &&
                                <>
                                    <DropdownItem className="border-top py-3"><a href="#" className="font-weight-bold">{t('contracts.milestones.title')}</a></DropdownItem>
                                    {milestones.map(milestone => (
                                        <DropdownItem className="py-3" id={milestone._id} data-action="milestone" onClick={handleChangeMilestone(milestone._id)}>
                                                <i
                                                    className={clsx(' mr-2 fas', milestone._id === milestoneId ? 'fa-check-circle text-primary' : 'fa-circle text-muted')}

                                                />
                                                {truncate(milestone.name, 20)} {/* {truncate name 20} */}
                                        </DropdownItem>
                                    ))}
                                    <DropdownItem className="py-3" data-action="milestone" onClick={handleChangeMilestone('')}>
                                            <i className="fas fa-circle text-muted mr-2" /> Ninguno
                                    </DropdownItem>
                                </>
                            }
                        </DropdownMenu>   
                    </UncontrolledDropdown>
            }
        </ElementMenuRoot>
    )
}



export const EditorBaseElementRoot = styled.div`
    border: 1px solid #e4e4e4;
    padding: ${props => props.type === 'signature' ? '' : '35px 25px'};
    margin-bottom: 15px;
    position: relative;
    cursor: pointer;  
    user-select: 'text';
    line-height: 1;
    border-radius: .25rem;
    border-color: ${props => props.active && props.editorMode === 'contract' ? '#f58543' : props.active && props.editorMode === 'template' ? '#5fa0d6' : '#e4e4e4'};
`

const SaveSuggestionWrapper = styled.div`
    position: absolute;
    padding: 0;
    margin: 0;
    top: -2px;
    left: -25px;
    display: block;
    user-select: none;
    i {
      width: 25px;
      height: 25px;
      text-align: center;
      border: 1px dashed;
      border-color: ${props => props.editorMode === 'template' ? '#5fa0d6' : '#f58543'};
      display: flex;
      justify-content: center;
      align-items: center;
      color: #ece023;
    }
`


 export const EditorBaseElement = ({ element, type, isEmpty, children, content, hasVariable, hasImage }) => {
    const [isAddSuggestionOpen, setIsAddSuggestionOpen] = useState(false);
    const { t } = useTranslation();
    const editor = useSlate()
    const { document: contract, mode: editorMode = 'contract', currentElement } = useEditorState();
    const { setCurrentElement } = useEditorActions();

    const currentElementId = currentElement._id;    

    const toggleAddSuggestion = () => setIsAddSuggestionOpen(open => !open);

    const handleClick = () => {
        try {
            if (!element) return;
    
            const [,elementPath] = getNodesById(editor, element._id)[0] || [0];
    
            const path = element.type === 'table-wrap' || element.type === 'table' ? [elementPath[0]] : elementPath.length < 2 ? elementPath : [elementPath[0],0];
    
            const location = editor.selection ? editor.selection.focus : { path, offset: 0 }
    
            if (editor.selection?.focus?.path[0] !== elementPath[0]) {
                Transforms.deselect(editor)
            }
          
            if (currentElementId !== element._id) {
                setCurrentElement({ element: element, location })
            }
        } catch (error) {
            
        }
    }
    

    return (
      <EditorBaseElementRoot 
          className={clsx('editor-canvas-item  editor-canvas-padding', currentElementId === element._id && 'active')} 
          active={currentElementId === element._id} 
          type={element.type}
          editorMode={editorMode} 
          onClick={handleClick} 
      >   
              <ElementMenu element={element} /> 
              { element.currentLocalVersion && element.currentLocalVersionId !== element.currentVersionId && <CompareContainer>
                <div className="mb-1">
                    <strong>Autor: </strong> 
                    {element.currentLocalVersion?.user}{' '}
                    <span class="createdAt">
                        {/* ({{momentAgo version.createdAt}}) */}
                        ({formatDistanceToNow(element.currentLocalVersion?.createdAt)})
                    </span>
                </div>

                <div dangerouslySetInnerHTML={{ __html: element.compareHTML }} />
              </CompareContainer> }
              {!isEmpty && element.type === 'clause' && currentElementId === element._id && 
              <SaveSuggestionWrapper 
                className="item-suggestion" 
                editorMode={editorMode} 
                title={t('editor.suggestions.save_tooltip')}  
                contentEditable={false} 
                onClick={toggleAddSuggestion}
              >
                  <i className="fas fa-star"></i>
              </SaveSuggestionWrapper>}
              {!isEmpty && element.type === 'clause' &&  
                <SaveSuggestionModal 
                  isOpen={isAddSuggestionOpen} 
                  onClose={toggleAddSuggestion} 
                  element={element} 
                  content={content} 
                /> }
              {children}
      </EditorBaseElementRoot>
    )
}


function SaveSuggestionModal({ element, content, isOpen, onClose }) {
    const { t } = useTranslation();
    const inputRef = useRef();
    const queryClient = useQueryClient();
    const { mutate: mutateSaveSuggestion } = useMutation((payload) => fetcher(addSuggestion(payload)), {
        onSuccess: () => {
            queryClient.invalidateQueries(['suggestions'])
            onClose()
            //TODO: go to suggestions $('.editor-sidebar-menu [data-action="suggestions"]').click();
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    });

    const handleSave = () => {
        const name = inputRef.current.value;

        if (!name.length) {
            toast.error(t('Debes ingresar un nombre para identificar a esta cláusula.'));
        }

        mutateSaveSuggestion({
            children: element.children,
            name
        })
    }

    return (
        <Modal isOpen={isOpen} contentEditable={false} centered size="md">
            <ModalHeader toggle={onClose} className="bg-light">
                {t('editor.suggestions.modal_title')}
            </ModalHeader>
            <ModalBody>
                <Card className="bg-light rounded p-3 mb-2">
                    {content}
                </Card>
                <p>
                    {t('editor.suggestions.modal_instruction')}
                </p>
                <FormGroup>
                    <label><b>{t('editor.suggestions.modal_name')}:</b></label>
                    <input
                        type="text"
                        ref={inputRef}
                        className="form-control input-sm"
                        placeholder={t('editor.suggestions.modal_name_placeholder')}
                        name="name"
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    <Button
                        onClick={onClose}
                    >
                        {t('editor.suggestions.modal_cancel')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSave}
                    >
                        <i className="fas fa-save"></i> {t('editor.suggestions.modal_save')}
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    )
}